import { useDisplayCurrency } from '../../hooks/useCurrency';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import {
  fetchInvoices,
  createInvoice,
  sendInvoice,
  voidInvoice,
  checkPaymentStatus,
} from '../../store/actions/FinanceActions';
import Table from '../shared/TailwindComponents/Table';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Input from '../shared/TailwindComponents/Input';
import { PiPlusLight, PiPaperPlaneTiltLight, PiProhibitLight, PiCopyLight, PiReceiptLight } from 'react-icons/pi';
import { FinanceInvoice } from '../../store/reducer/FinanceReducer';
import CreateInvoiceModal from './CreateInvoiceModal';
import InvoiceDetailModal from './InvoiceDetailModal';

interface Props {
  organizationId: number | undefined;
}

const formatCurrency = (amount: number, currency: string = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

const statusBadgeColor: Record<string, any> = {
  draft: 'gray',
  sent: 'blue',
  paid: 'green',
  overdue: 'red',
  void: 'yellow',
  partially_paid: 'purple',
};

const SummaryCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="glass rounded-lg p-4">
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className={`text-lg font-medium mt-1 ${highlight ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}>
      {value}
    </p>
  </div>
);

const InvoicesTab: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const invoices = useAppSelector((state: RootState) => state.finance.invoices);
  const account = useAppSelector((state: RootState) => state.finance.account);
  const loading = useAppSelector((state: RootState) => state.finance.loading);
  // One currency rule app-wide (hooks/useCurrency): the user's, else the firm's.
  const preferredCurrency = useDisplayCurrency();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [detailInvoiceId, setDetailInvoiceId] = useState<number | null>(null);
  const [editInvoice, setEditInvoice] = useState<FinanceInvoice | null>(null);

  // Compute invoice summary from current items
  const invoiceSummary = React.useMemo(() => {
    const items = invoices.items;
    const currency = items[0]?.currency || account?.default_currency || preferredCurrency;
    const totalInvoiced = items.reduce((sum: number, inv: FinanceInvoice) => sum + inv.total_amount, 0);
    const totalPaid = items.reduce((sum: number, inv: FinanceInvoice) => sum + inv.amount_paid, 0);
    const totalOutstanding = items.reduce((sum: number, inv: FinanceInvoice) => sum + inv.amount_due, 0);
    return { totalInvoiced, totalPaid, totalOutstanding, currency };
  }, [invoices.items, account]);

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchInvoices(organizationId, { page, status: statusFilter || undefined }));
    }
  }, [dispatch, organizationId, page, statusFilter]);

  // Listen for header "New Invoice" button
  useEffect(() => {
    const handler = () => setShowCreateModal(true);
    window.addEventListener('finance:new-invoice', handler);
    return () => window.removeEventListener('finance:new-invoice', handler);
  }, []);

  // Auto-check payment status when returning from Stripe (?paid=<id>)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paidId = params.get('paid');
    if (paidId && organizationId) {
      dispatch(checkPaymentStatus(Number(paidId), organizationId)).then(() => {
        dispatch(fetchInvoices(organizationId, { page, status: statusFilter || undefined }));
      });
      // Clean up the query param
      params.delete('paid');
      params.delete('session_id');
      const cleanSearch = params.toString();
      history.replace({ pathname: location.pathname, search: cleanSearch || undefined });
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = async (invoiceId: number) => {
    if (!organizationId) return;
    const result = await dispatch(sendInvoice(invoiceId, organizationId));
    if (result?.payment_url) {
      navigator.clipboard.writeText(result.payment_url);
    }
    dispatch(fetchInvoices(organizationId, { page, status: statusFilter || undefined }));
  };

  const handleVoid = async (invoiceId: number) => {
    if (!organizationId) return;
    await dispatch(voidInvoice(invoiceId, organizationId));
    dispatch(fetchInvoices(organizationId, { page, status: statusFilter || undefined }));
  };

  const copyPaymentLink = (link: string) => {
    navigator.clipboard.writeText(link);
  };

  const statusFilterOptions = [
    { value: '', label: t('All statuses') },
    { value: 'draft', label: t('Draft') },
    { value: 'sent', label: t('Sent') },
    { value: 'paid', label: t('Paid') },
    { value: 'overdue', label: t('Overdue') },
    { value: 'void', label: t('Void') },
  ];

  const columns = ['Invoice #', 'Status', 'Total', 'Due', 'Due Date', 'Created'];

  const tableData = invoices.items.map((inv: FinanceInvoice) => ({
    id: inv.id,
    'Invoice #': inv.invoice_number,
    Status: inv.status,
    Total: inv.total_amount,
    Due: inv.amount_due,
    'Due Date': inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—',
    Created: new Date(inv.created_at).toLocaleDateString(),
    _currency: inv.currency,
    _raw: inv,
  }));

  const renderCell = (column: string, value: any, row: any) => {
    switch (column) {
      case 'Invoice #':
        return <span className="font-medium text-gray-900 dark:text-white">{value}</span>;
      case 'Status':
        return (
          <Badge color={statusBadgeColor[value] || 'gray'}>
            {value}
          </Badge>
        );
      case 'Total':
        return <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(value, row._currency)}</span>;
      case 'Due':
        return <span className="text-gray-600 dark:text-gray-400">{formatCurrency(value, row._currency)}</span>;
      default:
        return value;
    }
  };

  const tableActions = [
    {
      label: t('Send'),
      icon: <PiPaperPlaneTiltLight className="w-4 h-4" />,
      onClick: (row: any) => handleSend(row.id),
      disabled: (row: any) => row._raw.status !== 'draft',
    },
    {
      label: t('Copy link'),
      icon: <PiCopyLight className="w-4 h-4" />,
      onClick: (row: any) => row._raw.stripe_payment_link && copyPaymentLink(row._raw.stripe_payment_link),
      disabled: (row: any) => !row._raw.stripe_payment_link,
    },
    {
      label: t('Void'),
      icon: <PiProhibitLight className="w-4 h-4" />,
      onClick: (row: any) => handleVoid(row.id),
      disabled: (row: any) => !['draft', 'sent', 'overdue'].includes(row._raw.status),
      isDestructive: true,
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 px-4 pt-4">
        <SummaryCard
          label={t('Total invoiced')}
          value={formatCurrency(invoiceSummary.totalInvoiced, invoiceSummary.currency)}
        />
        <SummaryCard
          label={t('Paid')}
          value={formatCurrency(invoiceSummary.totalPaid, invoiceSummary.currency)}
          highlight
        />
        <SummaryCard
          label={t('Outstanding')}
          value={formatCurrency(invoiceSummary.totalOutstanding, invoiceSummary.currency)}
        />
      </div>

      {/* Toolbar */}
      <div className="flex items-center px-4 py-3">
        <Dropdown
          options={statusFilterOptions}
          value={statusFilter}
          onChange={(val) => { setStatusFilter(val as string); setPage(1); }}
          placeholder={t('All statuses')}
          compact
          width="w-40"
        />
      </div>

      {/* Create Invoice Modal */}
      <CreateInvoiceModal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); setEditInvoice(null); }}
        onCreated={() => {
          setShowCreateModal(false);
          setEditInvoice(null);
          dispatch(fetchInvoices(organizationId!, { page: 1, status: statusFilter || undefined }));
          setPage(1);
        }}
        organizationId={organizationId!}
        editInvoice={editInvoice}
      />

      {/* Invoice Detail Modal */}
      <InvoiceDetailModal
        isOpen={detailInvoiceId !== null}
        onClose={() => setDetailInvoiceId(null)}
        invoiceId={detailInvoiceId}
        organizationId={organizationId!}
        onRefresh={() => {
          dispatch(fetchInvoices(organizationId!, { page, status: statusFilter || undefined }));
        }}
        onEdit={(invoice) => {
          setDetailInvoiceId(null);
          setEditInvoice(invoice);
          setShowCreateModal(true);
        }}
      />

      {/* Table */}
      <Table
        columns={columns}
        data={tableData}
        renderCell={renderCell}
        actions={tableActions}
        onRowClick={(row: any) => setDetailInvoiceId(row.id)}
        emptyState={{
          icon: <PiReceiptLight className="w-10 h-10" />,
          headline: t('No invoices yet'),
        }}
      />

      {/* Pagination */}
      {invoices.total_pages > 1 && (
        <div className="flex items-center justify-between px-4 py-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('Page')} {invoices.page} {t('of')} {invoices.total_pages} · {invoices.total} {t('total')}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="small"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              {t('Previous')}
            </Button>
            <Button
              variant="outline"
              size="small"
              onClick={() => setPage((p) => Math.min(invoices.total_pages, p + 1))}
              disabled={page >= invoices.total_pages}
            >
              {t('Next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesTab;
